import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Typography, Space, message, Popconfirm, InputNumber, Select, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, InboxOutlined, TeamOutlined, UploadOutlined, ClearOutlined, DatabaseOutlined } from '@ant-design/icons';
import { Product, Customer, Supplier } from '../types.ts';
import { productAPI, customerAPI, supplierAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import ProductAssociationsModal from '../components/ProductAssociationsModal.tsx';
import ProductImportModal from '../components/ProductImportModal.tsx';
import InventoryImportModal from '../components/InventoryImportModal.tsx';
import { formatCurrency, inputNumberFormatter, inputNumberParser } from '../utils/numberFormatting.ts';

const { Title } = Typography;
const { Search } = Input;

const Products: React.FC = () => {
  const { canWrite, user } = useAuth();
  const canEdit = canWrite('products');
  // Importazioni anagrafica prodotti: riservate all'amministratore (il backend lo impone)
  const isAdmin = user?.level_id === 1;
  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [inventoryImportModalVisible, setInventoryImportModalVisible] = useState(false);
  const [associationsModalVisible, setAssociationsModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<number | undefined>(undefined);
  const [selectedSupplier, setSelectedSupplier] = useState<number | undefined>(undefined);
  const [form] = Form.useForm();

  const fetchProducts = async (search?: string, customerId?: number, supplierId?: number) => {
    setLoading(true);
    try {
      const data = await productAPI.getProducts(search, customerId, supplierId);
      setProducts(data);
    } catch (error) {
      message.error('Errore nel caricamento prodotti');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const data = await customerAPI.getCustomers();
      setCustomers(data);
    } catch (error) {
      message.error('Errore nel caricamento clienti');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const data = await supplierAPI.getSuppliers();
      setSuppliers(data);
    } catch (error) {
      message.error('Errore nel caricamento fornitori');
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCustomers();
    fetchSuppliers();
  }, []);

  const handleAdd = () => {
    setEditingProduct(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    form.setFieldsValue({
      client_product_code: product.client_product_code,
      supplier_product_code: product.supplier_product_code,
      internal_product_code: product.internal_product_code,
      dogana_code: product.dogana_code,
      product_description: product.product_description,
      size: product.size,
      color: product.color,
      cost: product.cost,
      price: product.price,
      weight_gross: product.weight_gross,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await productAPI.deleteProduct(id);
      message.success('Prodotto eliminato con successo');
      fetchProducts(searchTerm, selectedCustomer, selectedSupplier);
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nell\'eliminazione del prodotto');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingProduct) {
        await productAPI.updateProduct(editingProduct.id, values);
        message.success('Prodotto aggiornato con successo');
      } else {
        await productAPI.createProduct(values);
        message.success('Prodotto creato con successo');
      }
      setModalVisible(false);
      fetchProducts(searchTerm, selectedCustomer, selectedSupplier);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Errore nell\'operazione';
      message.error(errorMessage);
    }
  };

  const handleAssociations = (product: Product) => {
    setSelectedProduct(product);
    setAssociationsModalVisible(true);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    fetchProducts(value, selectedCustomer, selectedSupplier);
  };

  const handleCustomerChange = (value: number | undefined) => {
    setSelectedCustomer(value);
    fetchProducts(searchTerm, value, selectedSupplier);
  };

  const handleSupplierChange = (value: number | undefined) => {
    setSelectedSupplier(value);
    fetchProducts(searchTerm, selectedCustomer, value);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCustomer(undefined);
    setSelectedSupplier(undefined);
    fetchProducts();
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Codice Interno',
      dataIndex: 'internal_product_code',
      key: 'internal_product_code',
      render: (code: string) => <strong>{code}</strong>,
    },
    {
      title: 'Codice Cliente',
      dataIndex: 'client_product_code',
      key: 'client_product_code',
      render: (code: string) => code || '-',
    },
    {
      title: 'Codice Fornitore',
      dataIndex: 'supplier_product_code',
      key: 'supplier_product_code',
      render: (code: string) => code || '-',
    },
    {
      title: 'Codice Dogana',
      dataIndex: 'dogana_code',
      key: 'dogana_code',
      render: (code: string) => code || '-',
    },
    {
      title: 'Descrizione',
      dataIndex: 'product_description',
      key: 'product_description',
      ellipsis: true,
    },
    {
      title: 'Taglia',
      dataIndex: 'size',
      key: 'size',
      render: (size: string) => size || '-',
    },
    {
      title: 'Colore',
      dataIndex: 'color',
      key: 'color',
      render: (color: string) => color || '-',
    },
    {
      title: 'Prezzo Acquisto',
      dataIndex: 'cost',
      key: 'cost',
      render: (cost: any) => {
        if (cost === null || cost === undefined || cost === '') return '-';
        return `€ ${formatCurrency(cost)}`;
      },
    },
    {
      title: 'Vendita',
      dataIndex: 'price',
      key: 'price',
      render: (price: any) => {
        if (price === null || price === undefined || price === '') return '-';
        return `€ ${formatCurrency(price)}`;
      },
    },
    {
      title: 'Peso Lordo',
      dataIndex: 'weight_gross',
      key: 'weight_gross',
      render: (weight: any) => {
        if (weight === null || weight === undefined || weight === '') return '-';
        return `${formatCurrency(weight)} kg`;
      },
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 160,
      render: (_, record: Product) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="default"
            size="small"
            icon={<TeamOutlined />}
            onClick={() => handleAssociations(record)}
            title="Gestisci Associazioni"
          />
          <Popconfirm
            title="Sei sicuro di voler eliminare questo prodotto?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sì"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>
          <InboxOutlined style={{ marginRight: 8 }} />
          Gestione Prodotti
        </Title>
        {canEdit && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {isAdmin && (
              <Button type="default" icon={<DatabaseOutlined />} onClick={() => setInventoryImportModalVisible(true)}>
                Importa da Giacenze
              </Button>
            )}
            {isAdmin && (
              <Button type="default" icon={<UploadOutlined />} onClick={() => setImportModalVisible(true)}>
                Importa Prodotti
              </Button>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Nuovo Prodotto
            </Button>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={24} md={10} lg={12} xl={14}>
            <Search
              placeholder="Cerca per codice interno, cliente, fornitore, dogana, descrizione, taglia o colore..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4} xl={3}>
            <Select
              placeholder="Cliente"
              allowClear
              style={{ width: '100%' }}
              size="large"
              value={selectedCustomer}
              onChange={handleCustomerChange}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={customers.map(customer => ({
                value: customer.id,
                label: customer.company
              }))}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4} xl={3}>
            <Select
              placeholder="Fornitore"
              allowClear
              style={{ width: '100%' }}
              size="large"
              value={selectedSupplier}
              onChange={handleSupplierChange}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={suppliers.map(supplier => ({
                value: supplier.id,
                label: supplier.name
              }))}
            />
          </Col>
          <Col xs={24} sm={8} md={2} lg={4} xl={4}>
            <Button 
              icon={<ClearOutlined />} 
              onClick={handleClearFilters}
              size="large"
              style={{ width: '100%' }}
            >
              Reset Filtri
            </Button>
          </Col>
        </Row>
      </div>

      <Table
        columns={canEdit ? columns : columns.filter((c) => c.key !== 'actions')}
        dataSource={products}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1500 }}
      />

      <Modal
        title={editingProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        destroyOnClose
        width={900}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="internal_product_code"
              label="Codice Interno"
              rules={[{ required: true, message: 'Codice interno richiesto' }]}
            >
              <Input placeholder="Codice univoco interno" />
            </Form.Item>
            
            <Form.Item
              name="client_product_code"
              label="Codice Cliente"
            >
              <Input placeholder="Codice del cliente" />
            </Form.Item>
            
            <Form.Item
              name="supplier_product_code"
              label="Codice Fornitore"
            >
              <Input placeholder="Codice del fornitore" />
            </Form.Item>
            
            <Form.Item
              name="dogana_code"
              label="Codice Dogana"
            >
              <Input placeholder="Codice doganale" />
            </Form.Item>
            
            <Form.Item
              name="size"
              label="Taglia"
            >
              <Input placeholder="XS, S, M, L, XL, XXL..." />
            </Form.Item>
            
            <Form.Item
              name="color"
              label="Colore"
            >
              <Input placeholder="Rosso, Blu, Verde..." />
            </Form.Item>
            
            <Form.Item
              name="cost"
              label="Prezzo di Acquisto (€)"
            >
              <InputNumber
                min={0}
                step={0.0001}
                precision={4}
                style={{ width: '100%' }}
                placeholder="0,0000"
                formatter={inputNumberFormatter}
                parser={inputNumberParser}
              />
            </Form.Item>

            <Form.Item
              name="price"
              label="Vendita (€)"
            >
              <InputNumber
                min={0}
                step={0.0001}
                precision={4}
                style={{ width: '100%' }}
                placeholder="0,0000"
                formatter={inputNumberFormatter}
                parser={inputNumberParser}
              />
            </Form.Item>

            <Form.Item
              name="weight_gross"
              label="Peso Lordo (kg)"
            >
              <InputNumber
                min={0}
                step={0.0001}
                precision={4}
                style={{ width: '100%' }}
                placeholder="0,0000"
                formatter={inputNumberFormatter}
                parser={inputNumberParser}
              />
            </Form.Item>
          </div>
          
          <Form.Item
            name="product_description"
            label="Descrizione Prodotto"
            rules={[{ required: true, message: 'Descrizione richiesta' }]}
          >
            <Input.TextArea rows={4} placeholder="Descrizione dettagliata del prodotto" />
          </Form.Item>
        </Form>
      </Modal>

      {selectedProduct && (
        <ProductAssociationsModal
          visible={associationsModalVisible}
          onCancel={() => {
            setAssociationsModalVisible(false);
            setSelectedProduct(null);
          }}
          productId={selectedProduct.id}
          productName={selectedProduct.product_description}
        />
      )}

      <ProductImportModal
        visible={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        onSuccess={() => {
          setImportModalVisible(false);
          fetchProducts(searchTerm, selectedCustomer, selectedSupplier);
        }}
      />

      <InventoryImportModal
        visible={inventoryImportModalVisible}
        onCancel={() => setInventoryImportModalVisible(false)}
        onSuccess={() => {
          setInventoryImportModalVisible(false);
          fetchProducts(searchTerm, selectedCustomer, selectedSupplier);
        }}
      />
    </div>
  );
};

export default Products;